import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Tour } from '../../mocks/tours';
import { useCart } from '../../hooks/useCart';

interface TourCardProps {
  tour: Tour;
}

const categoryLabels: Record<Tour['category'], string> = {
  gunubirlik: 'Günübirlik',
  konaklamali: 'Konaklamalı',
  ulasimli: 'Ulaşımlı Tatil',
  yurtdisi: 'Yurt Dışı',
};

export default function TourCard({ tour }: TourCardProps) {
  const { addToCart, isInCart } = useCart();
  const [added, setAdded] = useState(false);
  const inCart = isInCart(tour.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    addToCart(tour);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 border border-gray-100 dark:border-gray-700">
      {/* Image - Clickable */}
      <Link to={`/tur/${tour.id}`} className="block relative overflow-hidden h-52 cursor-pointer">
        <img
          src={tour.image}
          alt={tour.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className="bg-white/90 backdrop-blur-sm text-orange-500 text-xs font-semibold px-2.5 py-1 rounded-full">
            {categoryLabels[tour.category]}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <div className="w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full">
            <i className="ri-heart-line text-gray-500 text-sm" />
          </div>
        </div>
      </Link>

      {/* Info */}
      <div className="p-5">
        <Link to={`/tur/${tour.id}`} className="block cursor-pointer group">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-snug mb-3 line-clamp-2 min-h-10 group-hover:text-orange-500 transition-colors">
            {tour.name}
          </h3>
        </Link>

        {/* Meta */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-xs" />
            </div>
            <span className="text-xs">{tour.duration} {tour.durationUnit}</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-bus-line text-xs" />
            </div>
            <span className="text-xs">Ulaşım Dahil</span>
          </div>
        </div>

        <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed mb-4 line-clamp-2">
          {tour.description}
        </p>

        {/* Price + Buttons */}
        <div className="mt-auto">
          <div className="mb-3">
            <p className="text-xl font-bold text-orange-500">
              {tour.currency === 'TL'
                ? `₺${tour.price.toLocaleString('tr-TR')}`
                : `€${tour.price.toLocaleString('tr-TR')}`}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">kişi başı</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/tur/${tour.id}`}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold border border-orange-200 dark:border-orange-800 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line text-xs" />
              </div>
              Detay
            </Link>
            <button
              onClick={handleAddToCart}
              disabled={inCart || added}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                inCart || added
                  ? 'bg-green-500 text-white'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${inCart || added ? 'ri-check-line' : 'ri-shopping-cart-add-line'} text-xs`} />
              </div>
              <span>{inCart || added ? 'Eklendi' : 'Sepete Ekle'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
